import React, { useState } from "react";
import { orderBy, get, uniqueId } from "lodash";
import {
  Changes,
  HorseChange,
  JockeyChange,
  SurfaceChanges,
  BettingInterest
} from "@tvg/ts-types/Race";
import { formatPostTime, formatPeriod } from "@tvg/formatter/dates";
import { Icon, Paragraph, useColorTokens } from "@tvg/design-system";
import { Saddle } from "@tvg/design-system/web";
import {
  ChangesProcessed,
  processSurfaceRaceChanges
} from "../../ChangesCard/changesProcessor";
import {
  ChangesList,
  List,
  Time,
  Dt,
  Dl,
  ChangeLblContainer,
  ListItem,
  ScratchedHorse,
  ChangesWrapper,
  ChangesTitle,
  ShowAllChanges
} from "./styled-components";

interface Props {
  changes?: Changes;
  bettingInterests?: BettingInterest[];
  isFullWidth?: boolean;
  isSeeProfileAndPPModal?: boolean;
}

const getListElement = (
  change: ChangesProcessed | JockeyChange | HorseChange,
  bettingInterests?: BettingInterest[],
  color?: string
) => {
  let foundBI = null;
  let saddleColor = "";
  let numberColor = "";
  if ((change as HorseChange).scratched) {
    foundBI = bettingInterests?.find(
      (bInterest: BettingInterest) =>
        bInterest.biNumber === +(change as HorseChange).runnerId
    );

    if (foundBI) {
      saddleColor = foundBI?.saddleColor;
      numberColor = foundBI?.numberColor;
    }
  }

  return (
    <ListItem key={`changes-li-${change.date}-${uniqueId()}`}>
      <Time>
        {formatPostTime(change.date)} {formatPeriod(change.date)}
      </Time>
      <Dl>
        <Dt>
          {!(change as HorseChange).scratched ? (
            <>
              <Paragraph
                fontFamily="medium"
                qaLabel="change-title"
                isLineHigher
                color={color}
              >
                {(change as ChangesProcessed).keyword || "Jockey"}
              </Paragraph>
              <ChangeLblContainer>
                <Paragraph qaLabel="change-setTo" color={color}>
                  {(change as ChangesProcessed).oldValue || "Set to\u00A0"}
                </Paragraph>
                {(change as ChangesProcessed).newValue &&
                  (change as ChangesProcessed).oldValue && (
                    <Icon name="arrowRight" size="s" ml="4" mr="4" mt="2" />
                  )}
                {(change as ChangesProcessed).newValue && (
                  <strong>{(change as ChangesProcessed).newValue}</strong>
                )}
              </ChangeLblContainer>
            </>
          ) : (
            <>
              <Paragraph
                fontFamily="medium"
                qaLabel="change-title"
                color={color}
              >
                {(change as HorseChange).scratched ? "Scratch" : "Unscratch"}
              </Paragraph>
              <ChangeLblContainer isScratchedHorse>
                <Saddle
                  number={(change as HorseChange).runnerId || ""}
                  size="s"
                  numberColor={numberColor}
                  saddleColor={saddleColor}
                  mr="space-2"
                  ml="space-2"
                />
                <Paragraph qaLabel="change-setTo" color={color}>
                  <ScratchedHorse>
                    {(change as HorseChange).horseName}
                  </ScratchedHorse>
                </Paragraph>
              </ChangeLblContainer>
            </>
          )}
        </Dt>
      </Dl>
    </ListItem>
  );
};

const ChangesCardPro = (props: Props) => {
  const { changes, isFullWidth, isSeeProfileAndPPModal } = props;
  const { surface, horse } = changes || {};
  const jockey: JockeyChange[] = get(changes, "horse.jockey") || [];
  const scratched: HorseChange[] = get(changes, "horse.scratched") || [];
  const surfaceChanges: ChangesProcessed[] = processSurfaceRaceChanges(
    surface || ({} as SurfaceChanges)
  );
  const computedChanges = [...jockey, ...scratched, ...surfaceChanges];
  const [showAllChanges, setShowAllChanges] = useState(false);
  const color = useColorTokens("content.subtle");

  const countArrayLength = (arr: string | unknown[] | undefined) =>
    Array.isArray(arr) ? arr.length : 0;

  const countTotalChanges = () => {
    let totalChanges = 0;

    if (surface) {
      const { condition, tempRailDistance, course, distance } = surface;
      totalChanges += countArrayLength(condition);
      totalChanges += countArrayLength(tempRailDistance);
      totalChanges += countArrayLength(course);
      totalChanges += countArrayLength(distance);
    }

    if (horse) {
      totalChanges += countArrayLength(scratched);
      totalChanges += countArrayLength(jockey);
    }

    return totalChanges;
  };

  const hasMoreChanges = countTotalChanges() > 2;
  const noExtraChanges = countTotalChanges() <= 2;
  const expanded = !!showAllChanges;
  const orderedChanges = orderBy(computedChanges, ["date"], ["desc"]);

  return (
    <ChangesList
      isFullWidth={isFullWidth}
      isSeeProfileAndPPModal={isSeeProfileAndPPModal}
      expanded={expanded}
      noExtraChanges={noExtraChanges}
    >
      {isSeeProfileAndPPModal && (
        <ChangesWrapper>
          <ChangesTitle>Changes</ChangesTitle>
          {hasMoreChanges && !showAllChanges && (
            <ShowAllChanges
              onClick={() => setShowAllChanges(true)}
              data-qa-label="additional-changes-btn"
            >
              +{countTotalChanges() - 2} additional change
              {countTotalChanges() >= 2 ? "s" : ""}
            </ShowAllChanges>
          )}
        </ChangesWrapper>
      )}
      <List data-qa-label="changes-list">
        {orderedChanges
          .slice(0, 2)
          .map((change: ChangesProcessed | JockeyChange | HorseChange) =>
            getListElement(change, props.bettingInterests, color)
          )}
        {showAllChanges &&
          orderedChanges
            .slice(2)
            .map((change: ChangesProcessed | JockeyChange | HorseChange) =>
              getListElement(change, props.bettingInterests, color)
            )}
      </List>
    </ChangesList>
  );
};

export default ChangesCardPro;
